import React, { useState } from 'react';
import styled from 'styled-components';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 15px;
`;

const SummaryCard = styled.div`
  background: ${props => {
    if (props.$type === 'total') return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)';
    if (props.$type === 'clean') return 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)';
    if (props.$type === 'duplicate') return 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)';
    if (props.$type === 'bad') return 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)';
    return '#f5f5f5';
  }};
  border: 2px solid ${props => {
    if (props.$type === 'total') return '#2196f3';
    if (props.$type === 'clean') return '#4caf50';
    if (props.$type === 'duplicate') return '#ff9800';
    if (props.$type === 'bad') return '#f44336';
    return '#e0e0e0';
  }};
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const CardValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => {
    if (props.$type === 'total') return '#1976d2';
    if (props.$type === 'clean') return '#2e7d32';
    if (props.$type === 'duplicate') return '#f57c00';
    if (props.$type === 'bad') return '#c62828';
    return '#616161';
  }};
  margin-bottom: 8px;
`;

const CardLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #616161;
`;

const LoadMoreButton = styled.button`
  margin-top: 20px;
  background: #fff;
  color: #667eea;
  border: 2px solid #667eea;
  padding: 10px 24px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Segoe UI', sans-serif;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  margin-right: auto;
  
  &:hover {
    background: #667eea;
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
`;

const DetailsList = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  animation: slideDown 0.3s ease;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DetailSection = styled.div`
  margin-bottom: 20px;
  
  h4 {
    color: #424242;
    margin: 0 0 12px 0;
    font-size: 1rem;
    padding-bottom: 8px;
    border-bottom: 2px solid #e0e0e0;
  }
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e0e0e0;
  
  &:last-child {
    border-bottom: none;
  }
  
  .label {
    color: #616161;
    font-size: 0.9rem;
  }
  
  .value {
    color: #424242;
    font-weight: 600;
    font-size: 0.9rem;
  }
`;

const ProcessingPanel = ({ status }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  if (!status) return null;

  const calculateClean = () => {
    const total = status.total_records || 0;
    const bad = status.bad_data_count || 0;
    const duplicates = status.exact_duplicate_count || 0;
    return Math.max(0, total - bad - duplicates);
  };

  return (
    <div>
      <SummaryGrid>
        <SummaryCard $type="total">
          <CardValue $type="total">{status.total_records?.toLocaleString() || 0}</CardValue>
          <CardLabel>Total Records</CardLabel>
        </SummaryCard>
        
        <SummaryCard $type="clean">
          <CardValue $type="clean">{calculateClean().toLocaleString()}</CardValue>
          <CardLabel>Clean Records</CardLabel>
        </SummaryCard>
        
        <SummaryCard $type="duplicate">
          <CardValue $type="duplicate">{status.duplicate_count?.toLocaleString() || 0}</CardValue>
          <CardLabel>Duplicates</CardLabel>
        </SummaryCard>
        
        <SummaryCard $type="bad">
          <CardValue $type="bad">{status.bad_data_count?.toLocaleString() || 0}</CardValue>
          <CardLabel>Bad Data</CardLabel>
        </SummaryCard>
      </SummaryGrid>

      <LoadMoreButton onClick={() => setShowDetails(!showDetails)}>
        {showDetails ? (
          <>
            <FiChevronUp /> Hide Details
          </>
        ) : (
          <>
            <FiChevronDown /> Load More Details
          </>
        )}
      </LoadMoreButton>

      {showDetails && (
        <DetailsList>
          <DetailSection>
            <h4>🔄 Duplicate Analysis</h4>
            <DetailItem>
              <span className="label">Exact Duplicates</span>
              <span className="value">{status.exact_duplicate_count || 0}</span>
            </DetailItem>
            <DetailItem>
              <span className="label">Fuzzy Duplicates</span>
              <span className="value">{status.fuzzy_duplicate_count || 0}</span>
            </DetailItem>
            <DetailItem>
              <span className="label">Partial Duplicates</span>
              <span className="value">{status.partial_duplicate_count || 0}</span>
            </DetailItem>
          </DetailSection>
          
          <DetailSection>
            <h4>❌ Data Quality Issues</h4>
            <DetailItem>
              <span className="label">Missing Mandatory Fields</span>
              <span className="value">{status.missing_mandatory_count || 0}</span>
            </DetailItem>
            <DetailItem>
              <span className="label">Invalid Postal Codes</span>
              <span className="value">{status.invalid_postal_count || 0}</span>
            </DetailItem>
            <DetailItem>
              <span className="label">Invalid Addresses</span>
              <span className="value">{status.invalid_address_count || 0}</span>
            </DetailItem>
            <DetailItem>
              <span className="label">Suspicious Data</span>
              <span className="value">{status.suspicious_data_count || 0}</span>
            </DetailItem>
          </DetailSection>
          
          <DetailSection>
            <h4>📊 Data Profiling</h4>
            {status.profiling_summary && (
              <>
                <DetailItem>
                  <span className="label">Overall Quality Score</span>
                  <span className="value" style={{ color: '#4caf50' }}>
                    {status.profiling_summary.overall_quality_score || 0}%
                  </span>
                </DetailItem>
                <DetailItem>
                  <span className="label">Completeness Score</span>
                  <span className="value">{status.profiling_summary.completeness_score || 0}%</span>
                </DetailItem>
                <DetailItem>
                  <span className="label">Uniqueness Score</span>
                  <span className="value">{status.profiling_summary.uniqueness_score || 0}%</span>
                </DetailItem>
                <DetailItem>
                  <span className="label">Consistency Score</span>
                  <span className="value">{status.profiling_summary.consistency_score || 0}%</span>
                </DetailItem>
              </>
            )}
            <DetailItem>
              <span className="label">Data Quality Score</span>
              <span className="value" style={{ color: '#4caf50' }}>
                {status.data_quality_score || 0}%
              </span>
            </DetailItem>
          </DetailSection>
        </DetailsList>
      )}
    </div>
  );
};

export default ProcessingPanel;